import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import config, { ConfigInterface } from 'config/common';

export enum ConfigKeys {
    ECONOMIC_CALENDAR_ENABLED = 'isEconomicCalendarEnabled',
    SOLITICS_ENABLED = 'solitics.isSoliticsEnabled',
    SOLITICS_BRAND = 'solitics.brand',
    SOLITICS_TOKEN = 'solitics.token',
    SOLITICS_BRANCH = 'solitics.branch',
    INTERCOM_PLACEMENT = 'intercomPlacement',
    INTERCOM_APP_ID = 'intercomAppId',
    WNSTN_ENABLED = 'isWnstnEnabled',
}

type ConfigMapping = {
    [ConfigKeys.ECONOMIC_CALENDAR_ENABLED]: {
        envKey: 'REACT_APP_FEATURE_FLAGS_ECONOMIC_CALENDAR_IS_ENABLED';
        type: boolean;
    };
    [ConfigKeys.SOLITICS_ENABLED]: {
        envKey: 'REACT_APP_FEATURE_FLAGS_SOLITICS_IS_ENABLED';
        type: boolean;
    };
    [ConfigKeys.SOLITICS_BRAND]: {
        envKey: 'REACT_APP_SOLITICS_BRAND';
        type: string;
    };
    [ConfigKeys.SOLITICS_TOKEN]: {
        envKey: 'REACT_APP_SOLITICS_TOKEN';
        type: string;
    };
    [ConfigKeys.SOLITICS_BRANCH]: {
        envKey: 'REACT_APP_SOLITICS_BRANCH';
        type: string;
    };
    [ConfigKeys.INTERCOM_PLACEMENT]: {
        envKey: 'REACT_APP_INTERCOM_PLACEMENT';
        type: string;
    };
    [ConfigKeys.INTERCOM_APP_ID]: {
        envKey: 'REACT_APP_INTERCOM_APP_ID';
        type: string;
    };
    [ConfigKeys.WNSTN_ENABLED]: {
        envKey: 'REACT_APP_FEATURE_FLAGS_WNSTN_IS_ENABLED';
        type: boolean;
    };
};

type ConfigKey = keyof ConfigMapping;

export const useConfigValue = <K extends ConfigKey>(
    configPath: K,
    fallback: ConfigMapping[K]['type']
): ConfigMapping[K]['type'] => {
    const configEnv = useSelector((state: RootState) => state.config.env);

    return useMemo(() => {
        const mapping = getConfigMapping(configPath);
        if (!mapping) return fallback;

        const envValue = configEnv?.[mapping.envKey];
        if (envValue !== undefined && envValue !== null) {
            return envValue as ConfigMapping[K]['type'];
        }

        const staticValue = getNestedValue(config, configPath);
        if (staticValue !== undefined && staticValue !== null) {
            return staticValue as ConfigMapping[K]['type'];
        }

        return fallback;
    }, [configEnv, configPath, fallback]);
};

const getConfigMapping = (configPath: ConfigKey) => {
    const mappings: Record<ConfigKey, { envKey: string }> = {
        [ConfigKeys.ECONOMIC_CALENDAR_ENABLED]: { envKey: 'REACT_APP_FEATURE_FLAGS_ECONOMIC_CALENDAR_IS_ENABLED' },
        [ConfigKeys.SOLITICS_ENABLED]: { envKey: 'REACT_APP_FEATURE_FLAGS_SOLITICS_IS_ENABLED' },
        [ConfigKeys.SOLITICS_BRAND]: { envKey: 'REACT_APP_SOLITICS_BRAND' },
        [ConfigKeys.SOLITICS_TOKEN]: { envKey: 'REACT_APP_SOLITICS_TOKEN' },
        [ConfigKeys.SOLITICS_BRANCH]: { envKey: 'REACT_APP_SOLITICS_BRANCH' },
        [ConfigKeys.INTERCOM_PLACEMENT]: { envKey: 'REACT_APP_INTERCOM_PLACEMENT' },
        [ConfigKeys.INTERCOM_APP_ID]: { envKey: 'REACT_APP_INTERCOM_APP_ID' },
        [ConfigKeys.WNSTN_ENABLED]: { envKey: 'REACT_APP_FEATURE_FLAGS_WNSTN_IS_ENABLED' },
    };
    return mappings[configPath];
};

export const useDynamicConfig = (): ConfigInterface => {
    const configEnv = useSelector((state: RootState) => state.config.env);

    return useMemo(() => {
        const dynamicConfig = { ...config };

        if (configEnv) {
            if (configEnv.REACT_APP_FEATURE_FLAGS_ECONOMIC_CALENDAR_IS_ENABLED !== undefined) {
                dynamicConfig[ConfigKeys.ECONOMIC_CALENDAR_ENABLED] =
                    configEnv.REACT_APP_FEATURE_FLAGS_ECONOMIC_CALENDAR_IS_ENABLED;
            }

            if (configEnv.REACT_APP_FEATURE_FLAGS_WNSTN_IS_ENABLED !== undefined) {
                dynamicConfig[ConfigKeys.WNSTN_ENABLED] = configEnv.REACT_APP_FEATURE_FLAGS_WNSTN_IS_ENABLED;
            }

            if (configEnv.REACT_APP_FEATURES_FLAGS) {
                dynamicConfig.featuresFlags = {
                    ...dynamicConfig.featuresFlags,
                    ...configEnv.REACT_APP_FEATURES_FLAGS,
                };
            }
        }

        return dynamicConfig;
    }, [configEnv]);
};

const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
};
